import { useCollectionMutationContext } from '#pplx/frontend/apps/ask/hooks/collections-hooks/collection-mutations/useCollectionMutationContext.js';
import { usePrivatizeThreadIfEnterprise } from '#pplx/frontend/apps/ask/hooks/collections-hooks/collection-mutations/usePrivatizeThreadIfEnterprise.js';
import { upsertThreadCollection } from '#pplx/frontend/apps/ask/queries/collection-queries/upsertThreadCollection.js';
import { createCollection } from '#pplx/frontend/apps/ask/queries/mutations/collectionMutations.js';
import { TEMP_COLLECTION_UUID } from '#pplx/frontend/apps/ask/queries/query-keys/collectionConstants.js';
import { getUserCollectionsQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/collectionKeys.js';
import { getAllSpacesQueryKey, getListAtMentionsSpacesQueryKey, } from '#pplx/frontend/apps/ask/queries/query-keys/spacesKeys.js';
import { useMutation } from '@tanstack/react-query';
export const useCreateCollectionForThread = ({ reason, }) => {
    const { queryClient, listItemsKey, fetchByUUIDKey, getCachedCollectionInfo, updateResultsCache } = useCollectionMutationContext();
    const privatizeThreadIfEnterprise = usePrivatizeThreadIfEnterprise({
        reason,
    });
    return useMutation({
        mutationFn: async ({ entryUUID, title, description, emoji, instructions, accessLevel, callback, frontendContextUUID: _frontendContextUUID, }) => {
            const newCollection = await createCollection({
                params: {
                    title,
                    description,
                    emoji,
                    instructions,
                    accessLevel,
                },
                reason,
            });
            await upsertThreadCollection({
                params: {
                    new_collection_uuid: newCollection.uuid,
                    entry_uuid: entryUUID,
                    return_collection: false,
                    return_thread: false,
                },
                reason,
            });
            callback(newCollection);
            privatizeThreadIfEnterprise('add');
            return {
                title,
                description,
                emoji,
                instructions,
                accessLevel,
                newCollection,
            };
        },
        onMutate: async ({ entryUUID, frontendContextUUID, ...form }) => {
            // cancel inflight queries
            await queryClient.cancelQueries({ queryKey: listItemsKey });
            await queryClient.cancelQueries({
                queryKey: getUserCollectionsQueryKey(),
            });
            // cache current data
            const previousThreads = queryClient.getQueryData(listItemsKey);
            const previousCollections = queryClient.getQueryData(getUserCollectionsQueryKey());
            // prepend new collection to the list
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY_MIGRATION: Please fix in passing
            queryClient.setQueryData(getUserCollectionsQueryKey(), (oldData) => {
                oldData = oldData || { pages: [], pageParams: [] };
                const oldPages = (oldData && oldData.pages && oldData.pages[0]) ?? [];
                // creating a temp item and prepending it to the list
                const prevPages = oldPages?.length > 0 ? oldPages[0] : [];
                const newCollWithTmpId = {
                    ...prevPages,
                    ...form,
                    uuid: TEMP_COLLECTION_UUID,
                    thread_count: 1,
                };
                return {
                    ...oldData,
                    // infinite query, returns [][]Collection
                    pages: [[newCollWithTmpId, ...oldPages]],
                };
            });
            //  find thread and add new collection to it
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY_MIGRATION: Please fix in passing
            queryClient.setQueryData(listItemsKey, (oldData) => {
                oldData = oldData || { pages: [], pageParams: [] };
                const updatedPages = oldData.pages.map((page) => {
                    return page.map((thread) => {
                        return thread.uuid === entryUUID
                            ? {
                                ...thread,
                                collection: {
                                    ...thread.collection,
                                    description: form.description,
                                    uuid: TEMP_COLLECTION_UUID,
                                    title: form.title,
                                    emoji: form.emoji,
                                },
                            }
                            : thread;
                    });
                });
                return {
                    ...oldData,
                    pages: updatedPages,
                };
            });
            // collection can be created from library and a thread
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY_MIGRATION: Please fix in passing
            queryClient.setQueryData(fetchByUUIDKey(entryUUID), (oldData) => {
                const collInfo = oldData?.collection_info ?? {};
                return {
                    ...oldData,
                    collection_info: {
                        ...collInfo,
                        uuid: TEMP_COLLECTION_UUID,
                        description: form.description,
                        title: form.title,
                        emoji: form.emoji,
                    },
                };
            });
            // collection name in thread header comes from this query key
            // usecase:
            // - add to collections
            // - create new collection
            let previousCollectionInfo = null;
            if (frontendContextUUID) {
                // optimistically update the results cache
                previousCollectionInfo = getCachedCollectionInfo(queryClient, frontendContextUUID) ?? null;
                updateResultsCache(queryClient, frontendContextUUID, (message) => {
                    if (message.frontend_context_uuid === frontendContextUUID) {
                        return {
                            ...message,
                            collection_info: {
                                ...message.collection_info,
                                uuid: TEMP_COLLECTION_UUID,
                                title: form.title,
                                emoji: form.emoji,
                            },
                            bookmark_state: 'BOOKMARKED',
                        };
                    }
                    return message;
                });
            }
            return { previousThreads, previousCollections, previousCollectionInfo };
        },
        onSettled: (data, error, variables, context) => {
            if (error) {
                // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                queryClient.setQueryData(listItemsKey, context.previousThreads);
                queryClient.setQueryData(getUserCollectionsQueryKey(), 
                // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                context.previousCollections);
                if (variables.frontendContextUUID) {
                    // reset
                    updateResultsCache(queryClient, variables.frontendContextUUID, (message) => {
                        if (message.frontend_context_uuid === variables.frontendContextUUID) {
                            return {
                                ...message,
                                collection_info: context?.previousCollectionInfo ?? null,
                                bookmark_state: !context?.previousCollectionInfo ? 'NOT_BOOKMARKED' : 'BOOKMARKED',
                            };
                        }
                        return message;
                    });
                }
            }
            else {
                queryClient.invalidateQueries({ queryKey: listItemsKey });
                queryClient.invalidateQueries({
                    queryKey: getUserCollectionsQueryKey(),
                });
                queryClient.invalidateQueries({
                    queryKey: getListAtMentionsSpacesQueryKey(),
                });
                queryClient.invalidateQueries({ queryKey: getAllSpacesQueryKey() });
                // for ThreadHeader on /search/[...slug]
                if (variables.frontendContextUUID) {
                    updateResultsCache(queryClient, variables.frontendContextUUID, (message) => {
                        if (message.frontend_context_uuid === variables.frontendContextUUID) {
                            return {
                                ...message,
                                collection_info: {
                                    ...message.collection_info,
                                    uuid: data?.newCollection?.uuid,
                                    title: data?.newCollection?.title,
                                    emoji: data?.newCollection?.emoji,
                                    slug: data?.newCollection?.slug,
                                },
                                bookmark_state: 'BOOKMARKED',
                            };
                        }
                        return message;
                    });
                }
            }
        },
    });
};
//# sourceMappingURL=useCreateCollectionForThread.js.map